import React, { Fragment, JSX, useEffect, useRef, useState } from "react";
import { Link } from "react-router";
import { getStandardsByQuestionId } from "../../../api/standard.service";
import { IStandard } from "../../../model/interface/standard";
import {
  CaretUpSolidIcon,
  CaretDownSolidIcon,
  LinkIcon,
  UnlinkIcon,
} from "../../../assets/icons/index";
import Loader from "../../shared/loader/loader";
import Constant from "../../../utils/constant";
import SelectPageSize from "../../shared/common/pageSizeDropdown";
import Pagination from "../../shared/common/pagination";
import StandardSearch from "../../shared/common/standardSearch";
import { getSubjectAPI } from "../../../api/subject.service";
import ConfirmationPopup from "../../shared/common/confirmationPopup";
import { deleteAssociatedStandards } from "../../../api/question.service";

interface QuestionStandardProps {
  unLinkedStandardId: number;
  questionId: any;
  showUnApprovedSubject?: boolean;
  reloadStandards?: boolean;
  stateId?: number;
  subjectId?: number;
  domainId: number;
  contentAreaId: number;
  gradeLevel: string;
}

function QuestionStandard(props: QuestionStandardProps): JSX.Element {
  const defaultPageNumber = 1;
  const defaultPageSize = 10;
  const [pageSize, setPageSize] = useState(defaultPageSize);
  const [pageNumber, setPageNumber] = useState(defaultPageNumber);
  const [totalPages, setTotalPages] = useState<number>(0);
  const [standards, setStandards] = useState<IStandard[]>([]);
  const [sortAscending, setSortAscending] = useState<boolean>(true);
  const [orderByColumn, setOrderByColumn] = useState<string>("SubjectName");
  const [sortDir, setSortDir] = useState<string>("Asc");
  const paginationRef: any = useRef(undefined);
  const pageSizeRef: any = useRef(undefined);
  const [totalRecords, setTotalRecords] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(false);
  const [isLoaded, setIsLoaded] = useState<boolean>(false);
  const [includeUnapprovedSubject, setIncludeUnapprovedSubject] =
    useState<boolean>(true);
  const [associatedStandard, setAssociatedStandard] = useState<number>();
  const [gradeLevel, setGradeLevel] = useState<string>("");
  const [standardUnlink, setStandardUnlink] = useState<number>(0);
  const [showConfirmation, setShowConfirmation] = useState<boolean>(false);
  const [selectedStandards, setSelectedStandards] = useState<Array<number>>([]);
  const [selectAll, setSelectAll] = useState<boolean>(false);

  function handleSort(columnName) {
    setLoading(true);
    setOrderByColumn(columnName);
    setSortAscending(!sortAscending);
    setSortDir(sortAscending ? "DESC" : "ASC");
  }

  useEffect(() => {
    if (isLoaded) {
      getStandards(includeUnapprovedSubject);
    }
  }, [pageNumber]);

  useEffect(() => {
    if (isLoaded) {
      getStandards(includeUnapprovedSubject);
    }
  }, [props.reloadStandards, associatedStandard, props.unLinkedStandardId]);

  useEffect(() => {
    if (pageNumber === 1) {
      getSubject();
      getStandards(includeUnapprovedSubject);
      setIsLoaded(true);
    } else {
      paginationRef?.current?.onReset();
    }
  }, [pageSize]);

  useEffect(() => {
    if (isLoaded) {
      getStandards(includeUnapprovedSubject);
    }
  }, [sortDir]);

  const getSubject = () => {
    if (props.subjectId) {
      getSubjectAPI(props.subjectId).then((data) => {
        setGradeLevel(data?.data?.gradeLevel);
      });
    }
  };

  const getStandards = (includeUnapprovedSubject?: boolean) => {
    setLoading(true);
    setSelectAll(false);
    setSelectedStandards([]);
    getStandardsByQuestionId(
      props.questionId,
      pageSize,
      pageNumber,
      orderByColumn,
      sortDir,
      includeUnapprovedSubject
    ).then((data) => {
      if (data?.data?.data.length > 0) {
        data?.data?.data.forEach((standard) => {
          standard["isChecked"] = false;
        });
      }
      setStandards(data?.data?.data);
      setTotalPages(Math.ceil(data?.data?.totalRecords / pageSize));
      setTotalRecords(data?.data?.totalRecords);
      setLoading(false);
    });
  };

  function confirmStandardUnlink(idToUnlink?: number) {
    if (idToUnlink) {
      setShowConfirmation(true);
      setStandardUnlink(idToUnlink);
    }
  }

  const handleDelinkStandard = (confirmAction: boolean) => {
    setShowConfirmation(false);
    if (confirmAction) {
      if (selectedStandards && selectedStandards.length > 0) {
        setLoading(true);

        deleteAssociatedStandards({
          questionId: props.questionId,
          standardIds: selectedStandards.join(","),
        }).then(() => {
          getStandards(includeUnapprovedSubject);
        });
      }
    }
  };

  const handleUnApprovedSubjects = () => {
    setIncludeUnapprovedSubject(!includeUnapprovedSubject);
    getStandards(!includeUnapprovedSubject);
  };

  function handleSelectedStandards(standardId: number) {
    let updatedStandards: Array<number> = selectedStandards;
    if (updatedStandards.some((r) => r === standardId)) {
      updatedStandards = updatedStandards.filter((r) => r !== standardId);
    } else {
      updatedStandards.push(standardId);
    }

    const allStandards = [...standards];
    const standard = allStandards.find((s) => s.standardId === standardId);
    if (standard) {
      if (updatedStandards.indexOf(standardId) > -1)
        standard["isChecked"] = true;
      else standard["isChecked"] = false;
    }

    if (
      allStandards.length ===
      allStandards.filter((x) => x["isChecked"] === true).length
    ) {
      setSelectAll(true);
    } else {
      setSelectAll(false);
    }

    setSelectedStandards(updatedStandards);
    setStandards(allStandards);
  }

  function handleSelectAll() {
    const val = !selectAll;
    setSelectAll(val);
    const allStandards = [...standards];
    if (val) {
      allStandards.forEach((standard) => {
        standard["isChecked"] = true;
      });
    } else {
      allStandards.forEach((standard) => {
        standard["isChecked"] = false;
      });
    }

    const updatedStandards = allStandards
      .filter((s: any) => s.isChecked === true)
      .map((s) => s.standardId);

    setSelectedStandards(updatedStandards);
    setStandards(allStandards);
  }

  return (
    <Fragment>
      {loading && <Loader />}
      {showConfirmation && (
        <ConfirmationPopup
          title={"Unlink Associated Standard"}
          confirmAction={handleDelinkStandard}
          message={
            "Are you sure you want to unlink? It will unlink from all the linked standards."
          }
          buttonText={"Yes"}
        />
      )}
      <StandardSearch
        contentAreadId={props.contentAreaId}
        gradeLevel={props.gradeLevel}
        paramType="question"
        paramId={parseInt(props.questionId ?? 0)}
        updateStandardsList={() => {
          getStandards(includeUnapprovedSubject);
        }}
      ></StandardSearch>
      <div className="mt-4">
        <div className="col-span-6">
          <div className="flex justify-start">
            <h4 className="text-lg font-light text-gray-900">
              Associated Standards
            </h4>
            <h4 className="ml-4 text-lg font-light text-gray-900">
              <div className="flex gap-3 justify-end items-center absolute mt-2">
                <input
                  type="checkbox"
                  checked={includeUnapprovedSubject}
                  onClick={(e) => handleUnApprovedSubjects()}
                />
              </div>
              <span className="ml-6 mt-6">Show Un-approved Subjects</span>
            </h4>
          </div>
          <div className="flex justify-end">
            <button
              type="button"
              onClick={(e) => setShowConfirmation(true)}
              className="mt-2 inline-flex items-center text-sm text-white dark:text-white gap-2 bg-primary-color hover:bg-secondary-color py-1.5 px-4 rounded-lg"
            >
              Unassociate
            </button>
          </div>

          <div className="text-gray-900 block px-4 py-4 text-sm shadow border-b border-gray-200 rounded-b-lg">
            <div id="dataContainer" className="mt-4">
              <div className="flex flex-col mt-1">
                <div className="sm:-mx-6 lg:-mx-8">
                  <div className="align-middle inline-block min-w-full sm:px-6 lg:px-8">
                    <div className="shadow overflow-x-auto border-b border-gray-200 sm:rounded-lg">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-primary-color">
                          <tr>
                            <th></th>
                            <th className="pl-5 relative">
                              <input
                                type="checkbox"
                                checked={selectAll}
                                onClick={() => handleSelectAll()}
                              />
                            </th>
                            <th
                              scope="col"
                              className="px-3 py-2 pl-6 font-light text-sm text-left text-white dark:text-white tracking-wider"
                            >
                              <span className="flex gap-2">
                                Subject
                                <span className="cursor-pointer">
                                  {!sortAscending &&
                                  orderByColumn === "SubjectName" ? (
                                    <CaretUpSolidIcon
                                      onClick={() => handleSort("SubjectName")}
                                      className="w-5 h-5"
                                    ></CaretUpSolidIcon>
                                  ) : (
                                    <CaretDownSolidIcon
                                      onClick={() => handleSort("SubjectName")}
                                      className="w-5 h-5"
                                    ></CaretDownSolidIcon>
                                  )}
                                </span>
                              </span>
                            </th>
                            <th
                              scope="col"
                              className="px-3 py-2 pl-6 font-light text-sm text-left text-white dark:text-white tracking-wider"
                            >
                              <span className="flex gap-2">
                                State
                                <span className="cursor-pointer">
                                  {!sortAscending &&
                                  orderByColumn === "StateName" ? (
                                    <CaretUpSolidIcon
                                      onClick={() => handleSort("StateName")}
                                      className="w-5 h-5"
                                    ></CaretUpSolidIcon>
                                  ) : (
                                    <CaretDownSolidIcon
                                      onClick={() => handleSort("StateName")}
                                      className="w-5 h-5"
                                    ></CaretDownSolidIcon>
                                  )}
                                </span>
                              </span>
                            </th>
                            <th
                              scope="col"
                              className="px-3 py-2 pl-6 font-light text-sm text-left text-white dark:text-white tracking-wider"
                            >
                              <span className="flex gap-2">
                                Domain
                                <span className="cursor-pointer">
                                  {!sortAscending &&
                                  orderByColumn === "DomainName" ? (
                                    <CaretUpSolidIcon
                                      onClick={() => handleSort("DomainName")}
                                      className="w-5 h-5"
                                    ></CaretUpSolidIcon>
                                  ) : (
                                    <CaretDownSolidIcon
                                      onClick={() => handleSort("DomainName")}
                                      className="w-5 h-5"
                                    ></CaretDownSolidIcon>
                                  )}
                                </span>
                              </span>
                            </th>
                            <th
                              scope="col"
                              className="px-3 py-2 pl-6 font-light text-sm text-left text-white dark:text-white tracking-wider"
                            >
                              <span className="flex gap-2">
                                Standard
                                <span className="cursor-pointer">
                                  {!sortAscending &&
                                  orderByColumn === "Code" ? (
                                    <CaretUpSolidIcon
                                      onClick={() => handleSort("Code")}
                                      className="w-5 h-5"
                                    ></CaretUpSolidIcon>
                                  ) : (
                                    <CaretDownSolidIcon
                                      onClick={() => handleSort("Code")}
                                      className="w-5 h-5"
                                    ></CaretDownSolidIcon>
                                  )}
                                </span>
                              </span>
                            </th>

                            <th
                              scope="col"
                              className="px-3 py-2 font-light whitespace-wrap text-sm text-left text-white dark:text-white  "
                            >
                              Description
                            </th>

                            <th
                              scope="col"
                              className="px-3 py-2 font-light text-sm text-center text-white dark:text-white tracking-wider"
                            ></th>
                          </tr>
                        </thead>
                        <tbody>
                          {standards.map((standard: any, index) => (
                            <tr
                              key={standard.standardId}
                              className={
                                index % 2 === 0 ? "bg-white" : "bg-gray-100"
                              }
                            >
                              <td className="pl-5 relative">
                                {standard.isActive ? (
                                  <span className="mr-1 h-4/5 w-0.5  bg-green-500 rounded-md inline-block absolute top-0 left-1.5"></span>
                                ) : (
                                  <span className="mr-1 h-4/5 w-0.5  bg-red-500 rounded-md inline-block absolute top-0 left-1.5"></span>
                                )}
                                {standard.isLinked && (
                                  <LinkIcon
                                    className={`cursor-not-allowed w-5 ${
                                      standard.isLinked ? "" : "opacity-50"
                                    }`}
                                  />
                                )}
                              </td>
                              <td className="pl-5 relative">
                                <input
                                  type="checkbox"
                                  checked={standard.isChecked}
                                  onClick={() =>
                                    handleSelectedStandards(standard.standardId)
                                  }
                                />
                              </td>
                              <td className="px-3 py-2 whitespace-wrap text-sm text-left font-normal text-gray-500">
                                <Link
                                  className={`ml-1 cursor-pointer font-medium text-indigo-600 hover:text-indigo-500 hover:underline`}
                                  to={Constant.SUBJECTDETAIL.replace(
                                    ":id",
                                    standard.subjectId.toString()
                                  )}
                                >
                                  {standard.subjectName}
                                </Link>
                              </td>
                              <td className="px-3 py-2 whitespace-wrap text-sm text-left font-normal text-gray-500">
                                {standard.stateNames}
                              </td>

                              <td className="px-3 py-2 whitespace-wrap text-sm text-left font-normal text-gray-500">
                                <Link
                                  to={Constant.EDITDOMAIN.replace(
                                    ":id",
                                    standard.domainId
                                      ? standard.domainId.toString()
                                      : ""
                                  )}
                                  className="cursor-pointer font-medium text-indigo-600 hover:text-indigo-500 hover:underline"
                                >
                                  {standard.domainName}
                                </Link>
                              </td>
                              <td className="px-3 py-2  pl-6 whitespace-wrap text-sm text-left font-normal text-gray-500">
                                <Link
                                  to={Constant.EDITSTANDARD.replace(
                                    ":id",
                                    standard.standardId
                                      ? standard.standardId.toString()
                                      : ""
                                  )}
                                  className="cursor-pointer font-medium text-indigo-600 hover:text-indigo-500 hover:underline"
                                >
                                  {standard.standardIcon && (
                                    <img
                                      className="h-5 w-5 mr-2"
                                      src={standard.standardIcon}
                                    />
                                  )}
                                  [{standard.code}] {standard.name}
                                </Link>
                              </td>
                              <td className="px-3 py-2 whitespace-wrap text-sm font-normal text-gray-500">
                                {standard?.description}
                              </td>
                              {/* <td className="flex justify-center items-center w-12 px-3 py-2 whitespace-wrap text-sm text-center font-normal text-gray-700">
                                {
                                  <div className="flex gap-3 justify-end items-center absolute mt-4">
                                    <UnlinkIcon
                                      onClick={(e) =>
                                        confirmStandardUnlink(
                                          standard.standardId
                                        )
                                      }
                                      className="w-4 text-xs font-light cursor-pointer hover:text-red-500 transaction ease-in-out duration-300"
                                    ></UnlinkIcon>
                                  </div>
                                }
                              </td> */}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
                <div
                  className={`flex justify-between items-center ${
                    standards.length === 0 ? "hidden" : ""
                  }`}
                >
                  <Pagination
                    pages={totalPages}
                    setPageNumber={setPageNumber}
                    ref={paginationRef}
                  />
                  <span>
                    <span className="text-sm pr-4 ml-auto text-left-700">
                      Showing {pageNumber} to{" "}
                      {totalRecords < pageSize ? totalRecords : pageSize} of{" "}
                      {totalRecords} results
                    </span>

                    <SelectPageSize
                      setPageSize={setPageSize}
                      type={"second"}
                      ref={pageSizeRef}
                    />
                  </span>
                </div>

                {standards.length === 0 && (
                  <p className="p-2">No Records Found</p>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </Fragment>
  );
}

export default QuestionStandard;
